/**
 * Single global touch-gesture handler that owns the gestures NOT bound
 * to specific DOM elements (3-tap-anywhere → toggle privacy).
 *
 * Element-scoped gestures (swipe-between-tabs, swipe-between-portfolios,
 * double-tap a holding row) live in their respective parent components
 * via `useSwipe` / `useDoubleTap` from `utils/useGestures.js`.
 *
 * Mounted once below the Router in `App.js`. Renders nothing.
 *
 * Guards (same philosophy as KeyboardShortcuts):
 *   - bail when any `[role=dialog][data-state=open]` is present
 *   - bail when the user just typed in an INPUT/TEXTAREA/contenteditable
 *   - bail when the 3 taps were too far apart (>60px) — likely separate taps
 *   - the 3-tap window is 600ms (a deliberate triple-tap, not random clicks)
 *
 * Works on **touchscreens and mouse/trackpad clicks** (uses Pointer Events
 * API so all input types unify into the same code path).
 */
import { useEffect, useRef } from 'react';
import { usePrivacy } from '../contexts/PrivacyContext';
import { toast } from 'sonner';

const WINDOW_MS = 600;
const MAX_DRIFT_PX = 60;
// On mouse/trackpad, three rapid clicks fire ~150ms apart. We accept clicks
// + touch end so both touchscreen and mouse work. We rely on `pointerType`
// for fine-grained behaviour only when needed.

const isInteractive = (el) => {
  if (!el) return false;
  // Walk up the tree to see if the user actually tapped a button / link /
  // input / labelled control — those should keep their native behaviour.
  let cur = el;
  while (cur && cur !== document.body) {
    const tag = (cur.tagName || '').toUpperCase();
    if (tag === 'BUTTON' || tag === 'A' || tag === 'INPUT'
        || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'LABEL') {
      return true;
    }
    const role = cur.getAttribute && cur.getAttribute('role');
    if (role === 'button' || role === 'link' || role === 'switch'
        || role === 'checkbox' || role === 'tab' || role === 'menuitem') {
      return true;
    }
    if (cur.isContentEditable) return true;
    cur = cur.parentElement;
  }
  return false;
};

const isDialogOpen = () =>
  Boolean(document.querySelector('[role="dialog"][data-state="open"]'));

const GlobalGestures = () => {
  const { togglePrivacy, isMasked } = usePrivacy();
  const tapsRef = useRef([]); // [{ts, x, y}, …]

  useEffect(() => {
    const handle = (x, y, target) => {
      if (isInteractive(target)) return;
      if (isDialogOpen()) return;
      const now = Date.now();
      // Drop expired taps from the front of the buffer.
      const recent = tapsRef.current.filter((t) => now - t.ts <= WINDOW_MS);
      recent.push({ ts: now, x, y });
      // Bound to last 3 entries.
      while (recent.length > 3) recent.shift();
      tapsRef.current = recent;
      if (recent.length < 3) return;
      // Verify spatial coherence — all 3 within MAX_DRIFT_PX of each other.
      const [a, b, c] = recent;
      const drift = Math.max(
        Math.abs(b.x - a.x), Math.abs(b.y - a.y),
        Math.abs(c.x - a.x), Math.abs(c.y - a.y),
      );
      if (drift > MAX_DRIFT_PX) return;
      // FIRE! reset buffer so the next 3-tap is fresh.
      tapsRef.current = [];
      togglePrivacy();
      toast.success(
        // isMasked is the value BEFORE toggling — invert it for the user msg.
        isMasked ? 'Privacy off — figures visible' : 'Privacy on — figures hidden',
        { duration: 1500 },
      );
    };

    const onPointerUp = (e) => {
      // Ignore right-clicks / middle-clicks.
      if (e.button && e.button !== 0) return;
      handle(e.clientX, e.clientY, e.target);
    };

    document.addEventListener('pointerup', onPointerUp);
    return () => {
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [togglePrivacy, isMasked]);

  return null;
};

export default GlobalGestures;
export { GlobalGestures };
